import { useLocation, useParams, useRouteMatch } from 'react-router-dom'
import { memoize } from 'lodash'

import { useStore } from './storeHook'

import { required } from '../common/objectUtils'
import { isEnumKey, tryParseNumber, UnprocessedValueError } from '../common/tsUtils'
import { appRoutes, IntegrationHookParams, IntegrationParams, StoreRouteParams } from '../common/appRoutes'

import { StoreAccountModel } from '../types/accountProps'
import { integrationTypeByKey } from '../types/integrationTypeUtils'

import { WbAccountModel } from '../server/mpsklad_core/Models/WbAccountModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { OzonAccountModel } from '../server/mpsklad_core/Models/OzonAccountModel'
import { YandexMarketAccountModel } from '../server/mpsklad_core/Models/YandexMarketAccountModel'

export const useQuery =
  () => new URLSearchParams(useLocation().search)

export const useStoreRoute =
  (): StoreRouteParams | null => {
    // NOTE: Don't store in mobx - will either be outdated or conflict with React flow.
    const integrationRoute = useIntegrationRoute()

    return parseStoreRouteParams(integrationRoute)
  }

const parseStoreRouteParams =
  // memoization is required, otherwise we can't useEffect - it's a new object every time
  memoize(({integration, accountId}: Partial<IntegrationParams>) =>
      integration === IntegrationType.MoySklad || !integration || !accountId || accountId <= 0
      ? null
      : {integration, accountId},
    route => JSON.stringify(route))

/**
 * Must be used from within a *Router.
 */
export const useIntegrationRoute =
  (): Partial<IntegrationParams> => {
    const routeMatch = useRouteMatch<IntegrationHookParams>(appRoutes.Store.template)

    if (!routeMatch?.params) {
      return {}
    }

    const {integration, accountId} = routeMatch?.params

    // TODO: This is case-sensitive, make insensitive?
    if (!isEnumKey(integration, IntegrationType)) {
      return {}
    }

    switch (integration) {
      case 'Ozon':
      case 'Wildberries':
      case 'YandexMarket':
      case 'MoySklad':
        // Ok
        // MS is allowed for backwards compatibility (old routes)
        break

      default:
        throw new UnprocessedValueError('IntegrationTypeKey', integration)
    }

    const integrationType = integrationTypeByKey[integration]
    const accountIdParsed = tryParseNumber(accountId)

    if (accountIdParsed == null || accountIdParsed < 0) {
      return {integration: integrationType}
    }

    return {
      integration: integrationType,
      accountId: accountIdParsed
    }
  }

/**
 * Must be used from within an appropriate *Route.
 */
export const useAccountParamsOptional =
  (): Partial<IntegrationParams> => {
    const routeParams = useParams<IntegrationHookParams | undefined>()

    if (!routeParams) {
      return {}
    }

    const {integration, accountId} = routeParams

    // TODO: This is case-sensitive, make insensitive?
    const integrationParsed = isEnumKey(integration, IntegrationType) ? integrationTypeByKey[integration] : undefined

    let accountIdParsed = tryParseNumber(accountId)

    if (accountIdParsed != null && accountIdParsed <= 0) {
      accountIdParsed = undefined
    }

    return {
      integration: integrationParsed,
      accountId: accountIdParsed
    }
  }

/**
 * Must be used from within an appropriate *Route.
 */
export const useAccountParams =
  (): IntegrationParams => {
    let {integration, accountId} = useAccountParamsOptional()

    accountId = required(accountId)
    integration = required(integration)

    if (accountId <= 0) {
      throw new Error(`Bad accountId route param: ${accountId}`)
    }

    return {integration, accountId}
  }

/**
 * Must be used from within an appropriate <Route>.
 */
export const useStoreAccount =
  (): StoreAccountModel => {
    const accountParams = useAccountParams()

    return required(useTryFindStoreAccount(accountParams))
  }

/**
 * Must be used from within an appropriate Route.
 */
export const useStoreAccountOptional =
  () => {
    const accountParams = useAccountParamsOptional()

    return useTryFindStoreAccount(accountParams)
  }

const useTryFindStoreAccount =
  ({integration, accountId}: Partial<IntegrationParams>) => {
    const {syncStore: {ozonAccounts, wbAccounts, yandexMarketAccounts}} = useStore()

    if (accountId == null || accountId <= 0) {
      return undefined
    }

    switch (integration) {
      case undefined:
        return undefined

      case IntegrationType.Ozon:
        return ozonAccounts.find(_ => _.id === accountId)

      case IntegrationType.Wildberries:
        return wbAccounts.find(_ => _.id === accountId)

      case IntegrationType.YandexMarket:
        return yandexMarketAccounts.find(_ => _.id === accountId)

      case IntegrationType.MoySklad:
        throw new Error(`Bad IntegrationType: ${integration}`)

      default:
        throw new UnprocessedValueError('IntegrationType', integration)
    }
  }

/**
 * TODO: Can we merge these 6 hooks into one with some mapped types on generics?
 * Doesn't work: <T extends StoreIntegrationType>(integration: T): T extends IntegrationType.Ozon ? OzonAccountModel ...
 * Doesn't work: <T extends StoreIntegrationType>(integration: T): StoreAccountModelByType[T]
 * Doesn't work with `typeof integration` either
 * function overloads doesn't work with the above either
 */
export const useOzonAccount =
  (): OzonAccountModel => {
    const account = useStoreAccount()

    if (account.integrationType === IntegrationType.Ozon) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }

export const useWbAccount =
  (): WbAccountModel => {
    const account = useStoreAccount()

    if (account.integrationType === IntegrationType.Wildberries) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }

export const useYmAccount =
  (): YandexMarketAccountModel => {
    const account = useStoreAccount()

    if (account.integrationType === IntegrationType.YandexMarket) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }

export const useOzonAccountOptional =
  (): OzonAccountModel | undefined => {
    const account = useStoreAccountOptional()

    if (!account) {
      return undefined
    }

    if (account.integrationType === IntegrationType.Ozon) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }

export const useWbAccountOptional =
  (): WbAccountModel | undefined => {
    const account = useStoreAccountOptional()

    if (!account) {
      return undefined
    }

    if (account.integrationType === IntegrationType.Wildberries) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }

export const useYmAccountOptional =
  (): YandexMarketAccountModel | undefined => {
    const account = useStoreAccountOptional()

    if (!account) {
      return undefined
    }

    if (account.integrationType === IntegrationType.YandexMarket) {
      return account
    }

    throw new Error(`Bad IntegrationType: ${account.integrationType}`)
  }